import useDateTimeFormatter from '@/composables/useDateTimeFormatter.js'
import DispatchGridCells from '@/components/grid/DispatchGridCells.vue'


const { formatDateTime } = useDateTimeFormatter()

const toCityStateString = (location) =>
    location ? `${location?.city}, ${location?.state}` : ''

const stopsValueGetter = (params) => {
    const {
        current_stop = {},
        next_stop = {},
        first_stop = {},
        last_stop = {},
    } = params.data

    const locations = []

    if (current_stop && current_stop.location) {
        locations.push(toCityStateString(current_stop.location))
    }

    if (next_stop && next_stop.location) {
        locations.push(toCityStateString(next_stop.location))
    }

    if (first_stop && first_stop.location) {
        locations.push(toCityStateString(first_stop.location))
    }

    if (last_stop && last_stop.location) {
        locations.push(toCityStateString(last_stop.location))
    }

    return locations
}

const stopValueGetter = (params) => {
    // Determine which stop has called this getter by looking at the colDef 'field'
    const field = params.colDef?.field
    const stop = params.data[field]
    const location = stop?.location

    // ?s required below because some location properties have null values
    // return `${location?.name} - ${location?.city}, ${location?.state}`
    // returning an object so we can set character maximum on the name in the cellRenderer
    // including an ID because I assume that's what we should use to null out the renderer
    return {
        id: location?.id,
        name: location?.name,
        city: location?.city,
        state: location?.state,
        type: stop?.stop_type?.name,
    }
}

const stopFilterValueGetter = (params) => {
    const field = params.colDef?.field
    const stop = params.data[field]
    const location = stop?.location

    return [
        location?.id,
        location?.name,
        location?.city,
        location?.state,
        location?.code,
    ]
}

const toFilterableDate = (timestamp) => {
    return new Date(timestamp).setHours(0, 0, 0, 0)
}

const alertsFilterValueGetter = (params) => {
    let alerts = []
    if (params.data.summary?.has_unassigned_segment)
        alerts.push('Has unassigned segment')
    if (params.data.summary?.has_hazmat_shipment)
        alerts.push('Has hazmat shipment')
    if (params.data.summary?.has_backhaul_shipment)
        alerts.push('Has backhaul shipment')
    if (params.data.summary?.has_temperature_controlled_shipment)
        alerts.push('Has temp controlled shipment')
    if (params.data.comment_count > 0) alerts.push('Has load comments')
    if (params.data?.eta?.status == 'late') alerts.push('Late for Current Stop')
    if (params.data?.eta?.status == 'warn')
        alerts.push('Maybe late for Current Stop')
    if (alerts.length === 0) alerts.push('No alerts') // without this, you get a "(Blanks)" value in the Set filter list
    return alerts
}

const getQuickFilterTextValue = (params) => params.value

/**
 * The Ag-Grid column settings for the dispatch page.
 *
 * @type {Array}
 */
export const columns = [
    {
        field: 'id',
        minWidth: 160,
        checkboxSelection: true,
        headerName: 'Pro',
        headerCheckboxSelection: true,
        headerTooltip: 'Pro # of the load',
        hide: false,
        lockVisible: true,
        lockPosition: 'left',
        pinned: 'left',
        sortable: true,
        sortIndex: '1',
        suppressMovable: true,
        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                type: 'primary_column',
            },
        },
        cellClass: ({ data }) => {
            const status = data.eta?.status

            if (status === 'late') {
                return 'row-is-late'
            }

            if (status === 'warn') {
                return 'row-maybe-late'
            }
        },
        filter: 'agTextColumnFilter',
        getQuickFilterText: getQuickFilterTextValue,
    },
    {
        colId: 'from_to',
        headerName: 'From-To',
        headerTooltip: 'The first and last stop of the load.',
        valueGetter: (params) => {
            const load = params.data || {}
            const summary = load.summary || {}
            const firstStop = load.first_stop || {}
            const lastStop = load.last_stop || {}

            const stops = summary.stops?.total || 0
            const loadedMiles = summary.distances?.loaded || 0
            const totalMiles = loadedMiles + (summary.distances?.empty || 0)

            return {
                stops: stops,
                loaded_miles: loadedMiles,
                total_miles: totalMiles,
                start: `${firstStop?.location?.city}, ${firstStop?.location?.state}`,
                end: `${lastStop?.location?.city}, ${lastStop?.location?.state}`,
            }
        },
        valueFormatter: (params) => {
            const { start, end, stops } = params.value

            return `${start} to ${end} (${stops} stops)`
        },

        cellRenderer: DispatchGridCells,

        cellRendererParams: {
            extraParams: {
                type: 'from_to',
            },
        },
    },

    {
        colId: 'alerts',
        headerName: 'Alerts',
        headerComponent: DispatchGridCells,
        headerComponentParams: {
            extraParams: {
                type: 'alerts_header',
            },
        },
        headerTooltip: 'Load level alerts and notifications',
        filterValueGetter: alertsFilterValueGetter,
        filter: 'agSetColumnFilter',
        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                contextMenuFilter: false,
                type: 'alerts',
            },
        },
    },

    {
        field: 'creator.name',
        headerName: 'Creator',
        filter: 'agSetColumnFilter',
        sortable: true,
        filterParams: {
            defaultToNothingSelected: true,
        },
        getQuickFilterText: getQuickFilterTextValue,
    },
    {
        field: 'origination_terminal.name',
        headerName: 'Terminal',
        headerTooltip: 'Origination terminal of load',
        sortable: true,
        hide: false,
        filter: 'agSetColumnFilter',
        filterParams: {
            defaultToNothingSelected: true,
        },
        getQuickFilterText: getQuickFilterTextValue,
    },

    {
        field: 'summary.stops',
        headerName: 'Stops Arrived',
        type: 'rightAligned',
        sortable: true,
        filter: false,
        filterValueGetter: ({ data = {} }) => {
            return data.summary?.stops?.arrived
        },
        valueFormatter: ({ value }) => {
            return `${value?.arrived} / ${value?.total}`
        },
    },

    {
        field: 'first_purchase_order',
        cellDataType: 'text',
        tooltipField: 'first_purchase_order',
        headerName: '1st PO',
        headerTooltip: 'The first PO that appears on the load',
        filter: 'agTextColumnFilter',
        getQuickFilterText: getQuickFilterTextValue,
        maxWidth: 180,
        sortable: true,
    },

    {
        colId: 'current_assignment',
        headerName: 'Current Assignment',
        headerTooltip:
            'The assigned Driver(s)/Truck/Trailer(s) or Carrier for the Current Stop',
        valueGetter: ({ data }) => {
            return {
                drivers: data.drivers,
                truck: data.truck,
                trailers: data.trailers,
                carrier: data.carrier,
            }
        },
        filter: 'agTextColumnFilter',

        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                type: 'assignment_basic',
                contextMenuFilter: false,
            },
        },
        getQuickFilterText: (params) => {
            // need to change this to just the names and IDs I think
            return params.value.toString()
        },
    },

    {
        field: 'drivers',
        headerName: 'Current Driver HOS',
        headerTooltip:
            'HOS data (if available) for the Driver assigned to the Current Stop',
        valueGetter: ({ data }) => {
            if (!data.drivers) {
                return
            }

            const driver1 = data.drivers[0]
            const hos = driver1?.driver.hours_of_service

            return {
                duty_status: hos?.duty_status,
                on_duty_remaining: hos?.on_duty_remaining,
                drive_remaining: hos?.drive_remaining,
                updated_at: hos?.updated_at,
                data_supplier: hos?.data_supplier?.name,
            }
        },
        valueFormatter: ({ value }) => {
            if (value?.duty_status) {
                return `${value?.duty_status}, ${value?.drive_remaining}, ${value?.on_duty_remaining}`
            }
            return ''
        },
        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                type: 'hos',
            },
        },
        filter: false,
    },

    {
        field: 'category.name',
        headerName: 'Category',
        filter: 'agSetColumnFilter',
        filterParams: {
            defaultToNothingSelected: true,
        },
        sortable: true,
        getQuickFilterText: getQuickFilterTextValue,
    },

    {
        field: 'first_shipment_id',
        cellDataType: 'text',
        headerName: '1st Shipment ID',
        headerTooltip:
            'The Shipment ID of the first PO that appears on the load',
        filter: 'agTextColumnFilter',
        getQuickFilterText: getQuickFilterTextValue,
        width: 180,
        sortable: true,
    },

    {
        field: 'drivers',
        headerName: 'Current Driver(s)',
        headerTooltip: 'The driver on the current segment of the load',
        valueGetter: ({ data }) => {
            return data.drivers || []
        },
        filterValueGetter: ({ data }) => {
            return (data.drivers || []).map((driver) => {
                return driver?.name
            })
        },
        comparator: (valueA, valueB) => {
            if (
                !Array.isArray(valueA) ||
                !valueA.length ||
                !Array.isArray(valueB) ||
                !valueB.length
            ) {
                return 0
            }

            const driverA = valueA[0]?.last_name?.toLowerCase()
            const driverB = valueB[0]?.last_name?.toLowerCase()

            if (driverA < driverB) {
                return -1
            }

            if (driverA > driverB) {
                return 1
            }

            return 0
        },
        filter: 'agSetColumnFilter',
        filterParams: {
            defaultToNothingSelected: true,
        },
        sortable: true,
        sortIndex: 1,
        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                type: 'drivers',
            },
        },
        getQuickFilterText: (params) => {
            return params.value.toString()
        },
    },

    {
        field: 'current_location',
        headerName: 'Current Geo-location',
        headerTooltip: 'The geo-location of the current segment of the load',

        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                type: 'geo_location',
            },
        },
    },

    {
        field: 'truck.name',
        headerName: 'Current Truck',
        headerTooltip: 'The Truck on the current segment of the load',

        filter: 'agSetColumnFilter',
        sortable: true,
        filterParams: {
            defaultToNothingSelected: true,
        },
        getQuickFilterText: getQuickFilterTextValue,
    },

    {
        field: 'trailers',
        headerName: 'Current Trailer(s)',
        sortable: true,
        headerTooltip: 'The Trailer(s) on the current segment of the load',
        filter: 'agSetColumnFilter',
        valueGetter: (params) => {
            const trailers = params.data?.trailers || []

            return trailers
                .map((trailer) => trailer?.name)
                .filter((name) => !!name)
        },

        /** @TODO Remove old code */
        valueFormatter: ({ value }) => {
            return `${value}`
        },
        filterParams: {
            defaultToNothingSelected: true,
        },

        getQuickFilterText: (params) => params.value.toString(),
    },

    {
        field: 'carrier',
        headerName: 'Current Carrier',
        headerTooltip: 'The Carrier on the current segment of the load',
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
            defaultToNothingSelected: true,
        },

        valueFormatter: ({ value = {} }) => {
            return value?.name || ''
        },

        getQuickFilterText: ({ value }) => value?.name,
    },

    {
        field: 'current_location',
        headerName: 'Geo-location Timestamp',
        headerTooltip:
            'The time we last received geo-location data for this truck',

        cellRenderer: DispatchGridCells,
        sortable: true,
        cellRendererParams: {
            extraParams: {
                type: 'geo_timestamp',
            },
        },
    },

    {
        field: 'current_stop',
        headerName: 'Current Stop',
        headerTooltip: 'Current stop name, city, state',
        valueGetter: stopValueGetter,
        filterValueGetter: stopFilterValueGetter,
        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                type: 'stop_basic',
                stop: 'current_stop',
            },
        },
        filter: 'agTextColumnFilter',
        getQuickFilterText: getQuickFilterTextValue,
    },

    {
        field: 'current_stop.scheduled.arrive_at',
        headerName: 'Current Stop Sch. Arr.',
        type: 'rightAligned',
        sortable: true,
        // this takes the string and uses dayjs to create an object and return a formatted value
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY hh:mm A'),

        /// this takes the field string, creates a native JS date object,
        // and uses setHours(0,0,0,0) method so AG Grid filter works
        filter: 'agDateColumnFilter',
        valueGetter: (params) => {
            const arriveAt = params.data.current_stop?.scheduled?.arrive_at

            if (!arriveAt) {
                return ''
            }

            return new Date(arriveAt)
        },

        headerTooltip:
            'The scheduled arrival date & time of the current segment of the load',
    },

    {
        field: 'current_stop.scheduled.arrive_at',
        headerName: 'Current Stop Sch. Arr. Time',
        sortable: true,
        type: 'rightAligned',
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'hh:mm A'),

        headerTooltip:
            'The scheduled arrival time of the current segment of the load',
    },

    {
        field: 'current_stop.scheduled.arrive_at',
        headerName: 'Current Stop Sch. Arr. Date',
        sortable: true,
        type: 'rightAligned',
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY'),

        filter: 'agDateColumnFilter',
        valueGetter: (params) => {
            const arriveAt = params.data.current_stop?.scheduled?.arrive_at

            if (!arriveAt) {
                return ''
            }
            
            return toFilterableDate(arriveAt)
        },
        
        headerTooltip:
            'The scheduled arrival date of the current segment of the load',
    },

    {
        field: 'current_stop.actual.arrive_at',
        headerName: 'Current Stop Act. Arr.',
        sortable: true,
        type: 'rightAligned',
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY hh:mm A'),

        filterValueGetter: (params) => {
            return toFilterableDate(params.data.current_stop.actual?.arrive_at)
        },

        headerTooltip:
            'The actual arrival time of the current segment of the load',
    },

    {
        field: 'eta.projected_arrival',
        headerName: 'Current Stop ETA',
        headerTooltip:
            'The estimated time of arrival of the current segment of the load',
        type: 'rightAligned',
        sortable: true,
        filterValueGetter: (params) => {
            if (!params.data.eta) {
                return
            }

            return toFilterableDate(params.data.eta?.projected_arrival)
        },

        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY hh:mm A'),
    },

    {
        field: 'current_stop',
        // changing below from valueGetter to filterValueGetter because I want full current_stop object to pass to cellRenderer
        // this was I can use a single cellRenderer block for all the condensed stops (i.e. pass full object, execute generalized renderer)
        filterValueGetter: stopFilterValueGetter,
        headerName: 'Current Stop (Condensed View)',
        headerTooltip: 'Current stop rendered view...',
        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                type: 'stop_condensed',
            },
        },
        getQuickFilterText: getQuickFilterTextValue,
    },

    {
        field: 'next_stop',
        filterValueGetter: stopFilterValueGetter,
        headerName: 'Next Stop (Condensed View)',
        headerTooltip: 'Next stop rendered view...',
        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                type: 'stop_condensed',
            },
        },
        getQuickFilterText: getQuickFilterTextValue,
    },

    {
        field: 'next_stop.scheduled.arrive_at',
        headerName: 'Next Stop Sch. Arr.',
        sortable: true,
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY hh:mm A'),
        filter: 'agDateColumnFilter',
        valueGetter: (params) => {
            const arriveAt = params.data.next_stop?.scheduled?.arrive_at

            if (!arriveAt) {
                return ''
            }

            return new Date(arriveAt)
        },
    },

    {
        field: 'next_stop.scheduled.arrive_at',
        headerName: 'Next Stop Sch. Arr. Time',
        sortable: true,
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'hh:mm A'),
    },

    {
        field: 'next_stop.scheduled.arrive_at',
        headerName: 'Next Stop Sch. Arr. Date',
        sortable: true,
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY'),
        filter: 'agDateColumnFilter',
        valueGetter: (params) => {
            const arriveAt = params.data.next_stop?.scheduled?.arrive_at

            if (!arriveAt) {
                return ''
            }

            return toFilterableDate(arriveAt)
        },
    },

    {
        colId: 'revenue_per_mile',
        headerName: 'Revenue per mile',
        headerTooltip:
            "The load's total revenue per total mileage (loaded and unloaded).",
        type: 'rightAligned',
        valueGetter: (value) => {
            const revenues = value.data?.summary?.revenue || []

            const totalRevenue = revenues.reduce((previous, revenue) => {
                return (
                    previous +
                    revenue.line_haul +
                    revenue.fuel_surcharge +
                    revenue.miscellaneous +
                    revenue.accessorial
                )
            }, 0)

            const miles = value.data?.summary?.distances || {}

            if (!miles.loaded && !miles.empty) {
                return ''
            }

            const loadedMiles = Math.round(miles?.loaded)
            const totalMiles = Math.round(loadedMiles + miles?.empty)

            const revPerMile = totalRevenue / totalMiles

            return revPerMile.toFixed(2)
        },

        valueFormatter: ({ value }) => {
            if (value === '') {
                return ''
            }
            return `$${value}`
        },
        filter: 'agNumberColumnFilter',
        sortable: true,
    },

    {
        field: 'summary.distances',
        headerName: 'Miles (Loaded/Total)',
        headerTooltip: 'The mileage...',
        type: 'rightAligned',
        filter: 'agNumberColumnFilter',
        filterValueGetter: (params) => {
            let { distances = {} } = params.data?.summary || {}

            if (!distances.loaded && !distances.empty) {
                return 'TBD'
            }

            return Math.round(distances.loaded + distances.empty)
        },

        sortable: true,
        valueFormatter: ({ value = {} }) => {
            if (!value.loaded && !value.empty) {
                return ''
            }

            const loaded = Math.round(value?.loaded)
            const total = Math.round(loaded + value.empty)

            return `${loaded} mi / ${total} mi`
        },
    },

    {
        field: 'summary.revenue',
        headerName: 'Revenue',
        headerTooltip: "The load's total revenue amount.",
        type: 'rightAligned',
        valueGetter: (value) => {
            const revenues = value.data?.summary?.revenue || []

            const totalRevenue = revenues.reduce((previous, revenue) => {
                return (
                    previous +
                    revenue.line_haul +
                    revenue.fuel_surcharge +
                    revenue.miscellaneous +
                    revenue.accessorial
                )
            }, 0)

            return Math.round(totalRevenue)
        },

        valueFormatter: ({ value }) => `$${value}`,

        filter: 'agNumberColumnFilter',
        filterValueGetter: (value) => {
            const revenues = value.data?.summary?.revenue || []

            const total = revenues.reduce((previous, revenue) => {
                return (
                    previous +
                    revenue.line_haul +
                    revenue.fuel_surcharge +
                    revenue.miscellaneous +
                    revenue.accessorial
                )
            }, 0)

            return Math.round(total)
        },
        sortable: true,
    },

    {
        // field is supplied to filter for set value by default,
        // but valueGetter means we don't need field here, so that will be supplied (can also use filterValueGetter)
        // field: 'all_stops',
        colId: 'all_stops',
        headerTooltip: 'All stop locations for consolidated filtering...',
        headerName: 'All Stops City/State (First, Current, Next, Last)',
        valueGetter: stopsValueGetter,
        suppressColumnsToolPanel: true, // do not show in columns panel, even if it is in filters
        filterParams: {
            defaultToNothingSelected: true,
        },
    },

    {
        field: 'drivers',
        headerName: 'Current Assignment (Condensed View)',
        headerTooltip: 'Current Assignment rendered view.',
        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                type: 'assignment',
                contextMenuFilter: false,
            },
        },
        getQuickFilterText: getQuickFilterTextValue,
    },

    {
        field: 'first_stop.actual.depart_at',
        headerName: 'Created At',
        filter: 'agDateColumnFilter',
        sortable: true,
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY hh:mm A'),
    },

    {
        field: 'comment_count',
        headerName: 'Comment Count',
        sortable: true,
        getQuickFilterText: getQuickFilterTextValue,
    },

    {
        field: 'purchase_order_names',
        filter: false,
        headerName: 'Shipment Purchase Orders',
        headerTooltip:
            'The shipments that are attached to stops assigned to the load.',
        suppressColumnsToolPanel: true,
        getQuickFilterText: (params) => params.value.join(', '),
    },

    {
        field: 'next_stop',
        headerName: 'Next Stop',
        headerTooltip: 'Next stop name, city, state',
        valueGetter: stopValueGetter,
        filterValueGetter: stopFilterValueGetter,
        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                type: 'stop_basic',
                stop: 'next_stop',
            },
        },
        filter: 'agTextColumnFilter',
        getQuickFilterText: getQuickFilterTextValue,
    },

    {
        field: 'next_stop.occurred.arrive_at',
        headerName: 'Next Stop Act. Arr.',
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY hh:mm A'),
    },

    {
        field: 'first_stop',
        headerName: 'First Stop',
        headerTooltip: 'First stop name, city, state',
        valueGetter: stopValueGetter,
        filterValueGetter: stopFilterValueGetter,
        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                type: 'stop_basic',
                stop: 'first_stop',
            },
        },
        filter: 'agTextColumnFilter',
        getQuickFilterText: getQuickFilterTextValue,
    },

    {
        field: 'first_stop.scheduled.arrive_at',
        headerName: 'First Stop Sch. Arr.',
        sortable: true,
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY hh:mm A'),
        filter: 'agDateColumnFilter',
        valueGetter: (params) => {
            const arriveAt = params.data.first_stop?.scheduled?.arrive_at

            if (!arriveAt) {
                return ''
            }

            return new Date(arriveAt)
        },
    },

    {
        field: 'first_stop.scheduled.arrive_at',
        headerName: 'First Stop Sch. Arr. Time',
        sortable: true,
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'hh:mm A'),
    },

    {
        field: 'first_stop.scheduled.arrive_at',
        headerName: 'First Stop Sch. Arr. Date',
        sortable: true,
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY'),
        filter: 'agDateColumnFilter',
        valueGetter: (params) => {
            const arriveAt = params.data.first_stop?.scheduled?.arrive_at

            if (!arriveAt) {
                return ''
            }

            return toFilterableDate(arriveAt)
        },
    },

    {
        field: 'first_stop.actual.arrive_at',
        headerName: 'First Stop Act. Arr.',
        sortable: true,
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY hh:mm A'),
    },

    {
        field: 'last_stop',
        headerName: 'Last Stop',
        headerTooltip: 'Last stop name, city, state',
        valueGetter: stopValueGetter,
        filterValueGetter: stopFilterValueGetter,
        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                type: 'stop_basic',
                stop: 'last_stop',
            },
        },
        filter: 'agTextColumnFilter',
        getQuickFilterText: getQuickFilterTextValue,
    },

    {
        field: 'last_stop.scheduled.arrive_at',
        headerName: 'Last Stop Sch. Arr.',
        sortable: true,
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY hh:mm A'),
        filter: 'agDateColumnFilter',
        valueGetter: (params) => {
            const arriveAt = params.data.last_stop?.scheduled?.arrive_at

            if (!arriveAt) {
                return ''
            }

            return new Date(arriveAt)
        },
    },

    {
        field: 'last_stop.scheduled.arrive_at',
        headerName: 'Last Stop Sch. Arr. Time',
        sortable: true,
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'hh:mm A'),
    },

    {
        field: 'last_stop.scheduled.arrive_at',
        headerName: 'Last Stop Sch. Arr. Date',
        sortable: true,
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY'),
        filter: 'agDateColumnFilter',
        valueGetter: (params) => {
            const arriveAt = params.data.last_stop?.scheduled?.arrive_at

            if (!arriveAt) {
                return ''
            }

            return toFilterableDate(arriveAt)
        },
    },

    {
        field: 'last_stop.occurred.arrive_at',
        headerName: 'Last Stop Act. Arr.',
        sortable: true,
        valueFormatter: ({ value }) =>
            formatDateTime(value, 'MM/DD/YYYY hh:mm A'),
    },

    {
        field: 'status.name',
        headerName: 'Status',
        headerTooltip: 'Load Status',
        hide: false,
        sortable: true,
        cellRenderer: DispatchGridCells,
        cellRendererParams: {
            extraParams: {
                type: 'status',
            },
        },

        filter: 'agSetColumnFilter',
        filterParams: {
            values: ['In-Transit', 'Scheduled', 'Pending', 'Delivered'],
            defaultToNothingSelected: true,
        },
    },
]

/**
 * The grid's default tabs/views configuration.
 *
 * @type {Array}
 */
export const views = [
    {
        name: 'all',
        label: 'All Active',
        filterState: {
            'status.name': {
                values: ['In-Transit', 'Scheduled', 'Pending'],
                filterType: 'set',
            },
        },
        columnState: [
            {
                colId: 'status.name',
                width: 200,
                hide: false,
                pinned: null,
                sort: 'asc',
                sortable: true,
                sortIndex: 0,
            },
            {
                colId: 'alerts',
                hide: false,
            },

            {
                colId: 'terminal',
                hide: false,
            },

            {
                colId: 'summary.stops',
                hide: false,
            },

            {
                colId: 'first_purchase_order',
                hide: false,
            },

            {
                colId: 'current_assignment',
                hide: false,
            },

            {
                colId: 'drivers',
                hide: false,
            },

            {
                colId: 'current_location',
                hide: false,
            },

            {
                colId: 'current_stop',
                hide: false,
            },

            {
                colId: 'current_stop.scheduled.arrive_at',
                hide: false,
            },

            {
                colId: 'current_stop.actual.arrive_at',
                hide: false,
            },

            {
                colId: 'eta.projected_arrival',
                hide: false,
            },

            {
                colId: 'current_stop',
                hide: false,
            },

            {
                colId: 'next_stop.scheduled.arrive_at',
                hide: false,
            },

            {
                colId: 'revenue_per_mile',
                hide: false,
            },
        ],
    },
    {
        name: 'intransit',
        label: 'In Transit',
        filterState: {
            'status.name': {
                values: ['In-Transit'],
                filterType: 'set',
            },
        },
        columnState: [
            {
                colId: 'alerts',
                hide: false,
            },

            {
                colId: 'terminal',
                hide: false,
            },

            {
                colId: 'summary.stops',
                hide: false,
            },

            {
                colId: 'first_purchase_order',
                hide: false,
            },

            {
                colId: 'current_assignment',
                hide: false,
            },

            {
                colId: 'drivers',
                hide: false,
            },

            {
                colId: 'current_location',
                hide: false,
            },

            {
                colId: 'current_stop',
                hide: false,
            },

            {
                colId: 'current_stop.scheduled.arrive_at',
                hide: false,
            },

            {
                colId: 'current_stop.actual.arrive_at',
                hide: false,
            },

            {
                colId: 'eta.projected_arrival',
                hide: false,
            },

            {
                colId: 'current_stop',
                hide: false,
            },

            {
                colId: 'next_stop.scheduled.arrive_at',
                hide: false,
            },

            {
                colId: 'revenue_per_mile',
                hide: false,
            },
        ],
    },

    {
        name: 'scheduled',
        label: 'Scheduled',
        filterState: {
            'status.name': {
                values: ['scheduled'],
                filterType: 'set',
            },
        },
        columnState: [
            {
                colId: 'terminal',
                hide: false,
            },

            {
                colId: 'first_purchase_order',
                hide: false,
            },

            {
                colId: 'current_assignment',
                hide: false,
            },

            {
                colId: 'drivers',
                hide: false,
            },

            {
                colId: 'first_stop',
                hide: false,
            },

            {
                colId: 'first_stop.scheduled.arrive_at',
                hide: false,
            },

            {
                colId: 'revenue_per_mile',
                hide: false,
            },
        ],
    },

    {
        name: 'pending',
        label: 'Pending',
        filterState: {
            'status.name': {
                values: ['pending'],
                filterType: 'set',
            },
        },
        columnState: [
            {
                colId: 'terminal',
                hide: false,
            },

            {
                colId: 'first_purchase_order',
                hide: false,
            },

            {
                colId: 'first_stop',
                hide: false,
            },

            {
                colId: 'first_stop.scheduled.arrive_at',
                hide: false,
            },

            {
                colId: 'last_stop',
                hide: false,
            },

            {
                colId: 'last_stop.scheduled.arrive_at',
                hide: false,
            },

            {
                colId: 'revenue_per_mile',
                hide: false,
            },
        ],
    },

    {
        name: 'recently_completed',
        label: 'Recently Delivered',
        filterState: {
            'status.name': {
                values: ['delivered'],
                filterType: 'set',
            },
        },
        columnState: [
            {
                colId: 'terminal',
                hide: false,
            },

            {
                colId: 'from_to',
                hide: false,
            },

            {
                colId: 'first_purchase_order',
                hide: false,
            },

            {
                colId: 'current_assignment',
                hide: false,
            },

            {
                colId: 'summary.distances',
                hide: false,
            },

            {
                colId: 'revenue_per_mile',
                hide: false,
            },
        ],
    },
]
